/**
 * Cumulative Exposure Dashboard
 *
 * Tracks total PM2.5 inhaled across all runs.
 * Clean, data-focused layout — no glow, no gradients.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { calculateCumulativeExposure, type CumulativeExposure } from '@/utils/calculations/exposureCalculator';

interface ExposureDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ExposureDashboard({ isOpen, onClose }: ExposureDashboardProps) {
  const activityFeed = useRunningStore((s) => s.activityFeed);
  const [cumulative, setCumulative] = useState<CumulativeExposure | null>(null);

  useEffect(() => {
    if (!isOpen || activityFeed.length === 0) return;

    const data = calculateCumulativeExposure(
      activityFeed.map(a => ({
        duration_seconds: a.duration_seconds,
        distance_meters: a.distance_meters,
        aqi_data: a.aqi_data,
        pace_seconds_per_km: a.pace_seconds_per_km,
        activity_type: a.activity_type,
        created_at: a.created_at,
      }))
    );
    setCumulative(data);
  }, [isOpen, activityFeed]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[65] bg-[#0f1115] overflow-y-auto"
      >
        <div className="min-h-screen max-w-lg mx-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0f1115]/95 backdrop-blur-md px-5 py-4 flex items-center gap-4">
            <button onClick={onClose} className="p-1.5 -ml-1.5 text-slate-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-base font-semibold text-white">Exposure</h1>
          </div>

          <div className="px-5 pb-10">
            {!cumulative || activityFeed.length === 0 ? (
              <div className="text-center py-24">
                <h2 className="text-base font-medium text-white mb-2">No data yet</h2>
                <p className="text-sm text-slate-500 max-w-[260px] mx-auto">
                  Record your first run to start tracking breathing exposure.
                </p>
              </div>
            ) : (
              <>
                {/* Primary metric */}
                <div className="mb-8">
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-3 font-medium">Total PM2.5 inhaled</p>
                  <div className="bg-[#161920] rounded-xl p-5 border border-slate-800">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-4xl font-semibold text-white tabular-nums tracking-tight">{cumulative.totalPM25.toFixed(1)}</span>
                      <span className="text-sm text-slate-500">µg</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Across {cumulative.totalRuns} {cumulative.totalRuns === 1 ? 'run' : 'runs'} — equivalent to {cumulative.totalRestEquivalentHours}h of resting exposure
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="mb-8">
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-3 font-medium">Overview</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { v: cumulative.averagePerRun.toFixed(1), u: 'µg', l: 'Avg per run' },
                      { v: (cumulative.totalDistance / 1000).toFixed(1), u: 'km', l: 'Total distance' },
                      { v: cumulative.cleanestRunPM25.toFixed(1), u: 'µg', l: 'Cleanest run' },
                      { v: cumulative.dirtiestRunPM25.toFixed(1), u: 'µg', l: 'Highest exposure' },
                    ].map((item, i) => (
                      <div key={i} className="bg-[#161920] rounded-xl p-4 border border-slate-800">
                        <div className="flex items-baseline gap-1">
                          <span className="text-xl font-semibold text-white tabular-nums">{item.v}</span>
                          <span className="text-xs text-slate-600">{item.u}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">{item.l}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weekly trend */}
                <div className="mb-8">
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-3 font-medium">Weekly trend</p>
                  <div className="bg-[#161920] rounded-xl border border-slate-800 divide-y divide-slate-800/80">
                    {cumulative.weeklyTrend.map((week, i) => {
                      const maxPM25 = Math.max(...cumulative.weeklyTrend.map(w => w.totalPM25), 0.1);
                      const barPct = (week.totalPM25 / maxPM25) * 100;

                      return (
                        <div key={i} className="px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-slate-400">{week.weekLabel}</span>
                            <span className="text-xs text-slate-500 tabular-nums">
                              {week.totalPM25.toFixed(1)} µg · {week.totalRuns} {week.totalRuns === 1 ? 'run' : 'runs'}
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.max(1, barPct)}%` }}
                              transition={{ duration: 0.4, delay: i * 0.08 }}
                              className="h-full rounded-full bg-slate-500"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tips */}
                <div className="mb-8">
                  <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-3 font-medium">Reduce exposure</p>
                  <div className="bg-[#161920] rounded-xl p-4 border border-slate-800">
                    <ul className="space-y-2.5 text-xs text-slate-400">
                      {[
                        'Run early morning (5–7 AM) when AQI is lowest',
                        'Choose park trails over roadside routes',
                        'Run after rain — PM2.5 drops 30–50%',
                        'Avoid routes near heavy traffic during rush hours',
                      ].map((tip, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <span className="w-1 h-1 rounded-full bg-slate-600 mt-1.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
